import type { EventType } from '@cocoonspace/sdk-js/types/eventType'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import {
	getGetSpaceAvailabilityQueryKey,
	getGetSpaceCalendarQueryKey,
	getGetSpacePricesQueryKey,
	listSpaceAvailabilities,
} from '@cocoonspace/sdk-js/domains/availabilities/availabilities'
import {
	createBooking,
	getGetBookingQueryKey,
	getListBookingsQueryKey,
	updateBooking,
} from '@cocoonspace/sdk-js/domains/bookings/bookings'
import {
	createBookingWithEvent,
	getListEventsQueryKey,
} from '@cocoonspace/sdk-js/domains/events/events'
import {
	getListSpacesQueryKey,
	getSpace,
} from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { AvailabilitiesResponseDataItem } from '@cocoonspace/sdk-js/types/availabilitiesResponseDataItem'
import { SpaceHours } from '@cocoonspace/sdk-js/types/spaceHours'
import {
	getCartBooking,
	getGetCartBookingQueryKey,
} from '@cocoonspace/sdk-js/v2/domains/cart/cart'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import {
	type QueryClient,
	useMutation,
	useQueryClient,
} from '@tanstack/react-query'

import { useUserContext } from '~/providers/user-provider'
import { getWeekdayFromDate } from '~/utils/get-weekday-from-date'
import { getWallet } from '../account/components/wallet/use-wallet.hook'
import { shouldAddWallet } from '../booking/utils/should-add-wallet'

export interface BookingOption {
	day: string
	start?: string
	end?: string
}

const CART_RETRY_DELAYS = [100, 200, 400] as const

const wait = (milliseconds: number) =>
	new Promise<void>((resolve) => setTimeout(resolve, milliseconds))

const getCartBookingIdOrCreate = async () => {
	const cartBooking = await getCartBooking()
	const booking = cartBooking?.booking

	if (!booking?.id) {
		const { data } = await createBooking({})

		return { bookingId: data.id, eventsCount: 0 }
	}

	return {
		bookingId: booking.id,
		eventsCount: cartBooking?.eventsCount ?? 0,
	}
}

const refetchCartUntilEventsAreVisible = async ({
	queryClient,
	expectedEventsCount,
}: {
	queryClient: QueryClient
	expectedEventsCount: number
}) => {
	for (let attempt = 0; ; attempt += 1) {
		const cartBooking = await getCartBooking()

		queryClient.setQueryData(getGetCartBookingQueryKey(), cartBooking)

		if ((cartBooking?.eventsCount ?? 0) >= expectedEventsCount) return

		const delay = CART_RETRY_DELAYS[attempt]

		if (delay === undefined) return

		await wait(delay)
	}
}

const needsOpeningHours = (bookingOptions: BookingOption[]) =>
	bookingOptions.some(({ start, end }) => !(start && end))

const resolveSpaceWithHours = async (
	space: Pick<Space, 'id' | 'hours'>,
	bookingOptions: BookingOption[],
) => {
	if (!needsOpeningHours(bookingOptions) || space.hours) {
		return space
	}

	const { data } = await getSpace(space.id)

	return { ...space, hours: data.hours }
}

const computeOptions = (
	bookingOptions: BookingOption[],
	space: Pick<Space, 'id' | 'hours'>,
) => {
	return bookingOptions.map(({ day, start, end }) => {
		const date = dateTimeUtils(day)

		const basePayload = {
			day: date.customFormat('dateTech'),
			space: space.id,
			type: 'meeting' as EventType,
		}

		if (start && end) {
			return { ...basePayload, end, start }
		}

		const dayLabel = getWeekdayFromDate(day) as keyof SpaceHours

		const { from, to } = space.hours?.[dayLabel]?.open?.[0] ?? {
			from: '',
			to: '',
		}

		return {
			...basePayload,
			end: `${to}`,
			start: `${from}`,
		}
	})
}
export class SpaceNotAvailableError extends Error {
	constructor(public unavailable: AvailabilitiesResponseDataItem[]) {
		super('Space not available')
	}
}

export const useAddToCart = ({
	space,
}: {
	space: Pick<Space, 'id' | 'hours'>
}) => {
	const queryClient = useQueryClient()
	const { userQuery, isBusiness } = useUserContext()

	const defaultRefundModeIsWallet =
		userQuery.data?.default_refund_mode === 'wallet'

	return useMutation({
		mutationFn: async ({
			bookingOptions,
		}: {
			bookingOptions: BookingOption[]
		}) => {
			const spaceWithHours = await resolveSpaceWithHours(space, bookingOptions)

			// Compute events payload to the right format for the API
			const eventsPayload = computeOptions(bookingOptions, spaceWithHours)

			// Check availabillity (just in case)
			const unavailable = await listSpaceAvailabilities(eventsPayload).then(
				({ data }) => data.filter(({ available }) => !available),
			)

			// If there are unavailable slots, return an error
			if (unavailable.length) {
				throw new SpaceNotAvailableError(unavailable)
				// return Promise.reject({
				// 	data: unavailable,
				// 	message: 'unavailable',
				// })
			}

			// Get the booking ID or create a new one
			const { bookingId, eventsCount } = await getCartBookingIdOrCreate()

			// Create events for this booking
			const eventsQueries = eventsPayload.map(({ day, start, end, space }) => {
				return createBookingWithEvent({
					booking: bookingId,
					day,
					end,
					space,
					start,
				}).then(({ data }) => data)
			})

			const events = await Promise.all(eventsQueries)
			const wallet = await getWallet()

			if (
				shouldAddWallet({
					isBusiness,
					events,
					defaultRefundModeIsWallet,
					walletAmount: wallet?.amount,
				})
			) {
				await updateBooking(bookingId, { has_wallet: true })
				// if (hasWalletRefundOnBooking) {
				// } else {
				// 	const updateEventQueries = events.map((event) =>
				// 		updateEventExtra(event.id, 'WALLET', { quantity: 1 }),
				// 	)
				// 	await Promise.all(updateEventQueries)
				// }
			}

			await refetchCartUntilEventsAreVisible({
				expectedEventsCount: eventsCount + events.length,
				queryClient,
			})

			return { bookingId, events }
		},
		onSuccess: ({ bookingId }) => {
			const toRefetch = [
				getListEventsQueryKey({ booking: bookingId }),
				getGetBookingQueryKey(bookingId),
				getListBookingsQueryKey(),
				getGetSpaceCalendarQueryKey(space.id),
				getGetSpaceAvailabilityQueryKey(space.id),
				getGetSpacePricesQueryKey(space.id),
				getGetCartBookingQueryKey(),
				getListSpacesQueryKey(),
			]

			for (const queryKey of toRefetch) {
				queryClient.invalidateQueries({ queryKey })
			}
		},
	})
}
